/* Allure Home — Phase 2 editorial revamp.
   Full-bleed B&W editorial portrait hero with IssueSeal + chapter eyebrow,
   then continued editorial chapters (Services teaser, Reviews) on cream paper.
   Preserves all existing API calls (/services, /reviews/public). */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { api } from "@/lib/api";
import { tenantHref } from "@/lib/tenant";
import {
  ChapterEyebrow, DisplayHeadline, GoldRule, GoldFoilButton, HairlineButton,
  RotatedCaption, FilmGrain, IssueSeal, EditorialNumeral, MaisonIntro, ScrollScene,
} from "@/components/editorial";
import useMouseParallax from "@/hooks/useMouseParallax";
import useEditorialReveal from "@/hooks/useEditorialReveal";
import useStructuredData from "@/hooks/useStructuredData";
import { allureLocalBusinessSchema } from "@/lib/schemas";
import { EDITORIAL_ASSETS } from "@/lib/editorialAssets";

const HERO = "/hero/allure-home-hero.jpg";

// Map a service name/slug to an editorial AI accent image. The actual hero
// stays as the real founder portrait — AI imagery is reserved for these
// secondary "editorial accent" slots per Phase 1 brief.
function accentFor(svc) {
  const k = `${svc.slug || ""} ${svc.name || svc.title || ""}`.toLowerCase();
  if (k.includes("matern")) return EDITORIAL_ASSETS.editorial_maternity;
  if (k.includes("wed") || k.includes("couple") || k.includes("engage")) return EDITORIAL_ASSETS.editorial_couple_warm;
  if (k.includes("boudoir") || k.includes("intim")) return EDITORIAL_ASSETS.editorial_back_pose;
  return EDITORIAL_ASSETS.editorial_bw_portrait;
}

export default function Home() {
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);
  useEditorialReveal();
  useStructuredData(allureLocalBusinessSchema());

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data || [])).catch(() => setServices([]));
    api.get("/reviews/public?limit=24").then((r) => setReviews(r.data || [])).catch(() => setReviews([]));
  }, []);

  const featured = services.slice(0, 3);
  const featuredReviews = reviews.slice(0, 2);
  const parallax = useMouseParallax({ strength: 10, damping: 0.06 });

  return (
    <div className="bg-[color:var(--cream)] text-[color:var(--cream-ink)]">
      <MaisonIntro surface="allure" />
      {/* HERO — full-bleed editorial portrait */}
      <section className="relative min-h-screen overflow-hidden" data-testid="home-hero">
        <img
          src={HERO}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
          fetchpriority="high"
          decoding="async"
          style={{
            transform: `translate3d(${parallax.x}px, ${parallax.y}px, 0) scale(1.05)`,
            willChange: "transform",
          }}
        />
        {/* legibility gradients — darker on bottom-left where text lives */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0E0D0C]/85 via-[#0E0D0C]/35 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0E0D0C]/80 via-transparent to-transparent" />
        <FilmGrain opacity={0.06} tone="cool" />

        {/* IssueSeal top-right */}
        <div className="absolute top-24 right-6 lg:right-12 z-20">
          <IssueSeal issue="01" season="Spring" year="2026" tone="cream" size={120} />
        </div>

        {/* Rotated SCROLL caption lower-right */}
        <div className="hidden lg:block absolute right-4 bottom-16 z-20">
          <RotatedCaption side="right" tone="cream">SCROLL ↓</RotatedCaption>
        </div>

        {/* Lower-left text block */}
        <div className="relative z-10 max-w-[1480px] mx-auto px-6 lg:px-12 min-h-screen flex items-end pb-20 lg:pb-28 pt-32">
          <div className="max-w-2xl text-[color:var(--cream)]">
            <GoldRule width="2rem" />
            <div className="mt-3">
              <ChapterEyebrow chapter="I" label="Studio" />
            </div>
            <h1 className="ed-display mt-7" data-reveal="rise">
              Crafted with care.<br />Captured with <em>intent</em>.
            </h1>
            <p className="font-serif italic text-xl md:text-2xl mt-8 max-w-xl text-[color:var(--cream)]/85" data-reveal="rise">
              Allure Image is an editorial photography studio crafting refined portraits, brand stories, and life's milestones.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4" data-reveal="rise">
              <GoldFoilButton href={tenantHref("/booking")}>Book a session</GoldFoilButton>
              <HairlineButton href={tenantHref("/services")} tone="dark">View packages</HairlineButton>
            </div>
          </div>
        </div>
      </section>

      {/* SCROLL-SCENE STORY — three sticky cinematic reveals */}
      <ScrollScene
        scenes={[
          {
            image: EDITORIAL_ASSETS.editorial_bw_portrait.src,
            headline: "Crafted with intention",
            sub: "Every frame begins with a story.",
          },
          {
            image: EDITORIAL_ASSETS.editorial_maternity.src,
            headline: "Created together",
            sub: "Your moments, witnessed and held with care.",
          },
          {
            image: EDITORIAL_ASSETS.hero_allure_image_main.src,
            headline: "Begin your session",
            sub: "Reach out — let's plan what we'll remember.",
            cta: { label: "Book a consultation", href: "/booking" },
          },
        ]}
      />

      {/* CHAPTER II — SERVICES TEASER */}
      {featured.length > 0 && (
        <section className="relative border-t border-[color:var(--cream-ink)]/10 py-24 lg:py-32" data-testid="home-services">
          <FilmGrain opacity={0.035} tone="warm" />
          <div className="relative max-w-[1480px] mx-auto px-6 lg:px-12">
            <ChapterEyebrow chapter="II" label="A short menu" />
            <GoldRule width="3rem" className="mt-4 mb-8" />
            <DisplayHeadline size="hero" className="max-w-3xl" data-reveal="rise">
              The work we love <em>making</em>.
            </DisplayHeadline>
            <div className="mt-16 grid md:grid-cols-3 gap-y-12">
              {featured.map((s, i) => {
                const accent = accentFor(s);
                return (
                <div key={s.id || s.slug || i} data-reveal="rise"
                  className={`px-6 lg:px-10 ${i > 0 ? "md:border-l border-[color:var(--cream-ink)]/12" : ""}`}>
                  {/* Phase 1 editorial accent — chosen by service type. */}
                  <div className="overflow-hidden mb-7 aspect-[4/5]">
                    <img
                      src={accent.src}
                      alt={accent.alt}
                      width={accent.w}
                      height={accent.h}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover transition-transform duration-700 hover:scale-[1.03]"
                      style={{ transform: "translateZ(0)" }}
                    />
                  </div>
                  <EditorialNumeral value={String(i + 1).padStart(2, "0")} size="lg" style="outline" tone="gold" />
                  <h3 className="font-serif text-2xl md:text-3xl mt-5 leading-tight">{s.name || s.title || "Editorial sitting"}</h3>
                  <GoldRule width="2rem" className="mt-4 mb-4" />
                  <p className="ed-body-lg text-[color:var(--cream-ink)]/75 line-clamp-4">
                    {s.description || s.summary || "A considered session, kept by hand."}
                  </p>
                  <Link to={tenantHref("/services")} className="mt-5 inline-block ed-eyebrow text-[color:var(--gold-base)] hover:text-[color:var(--gold-deep)]">
                    Read more →
                  </Link>
                </div>
                );
              })}
            </div>
            <div className="mt-16 text-center">
              <HairlineButton href={tenantHref("/services")}>The full menu</HairlineButton>
            </div>
          </div>
        </section>
      )}

      {/* CHAPTER III — REVIEWS */}
      {featuredReviews.length > 0 && (
        <section className="relative border-t border-[color:var(--cream-ink)]/10 py-24 lg:py-32 bg-[color:var(--cream-paper)]" data-testid="home-reviews">
          <FilmGrain opacity={0.035} tone="warm" />
          <div className="relative max-w-[1480px] mx-auto px-6 lg:px-12">
            <ChapterEyebrow chapter="III" label="In their own words" />
            <GoldRule width="3rem" className="mt-4 mb-12" />
            <div className="grid md:grid-cols-2 gap-12">
              {featuredReviews.map((r, i) => (
                <figure key={r.id || i} data-reveal="rise" className="max-w-xl">
                  <blockquote className="font-serif italic text-2xl md:text-3xl leading-snug text-[color:var(--cream-ink)]">
                    “{r.body || r.text || "A morning that felt like art."}”
                  </blockquote>
                  <GoldRule width="2rem" className="mt-6 mb-4" />
                  <figcaption className="ed-eyebrow text-[color:var(--gold-deep)]">
                    {(r.author_name || r.name || "Anonymous").toUpperCase()}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CHAPTER IV — CLOSING */}
      <section className="relative border-t border-[color:var(--cream-ink)]/10 py-24 lg:py-32 text-center">
        <FilmGrain opacity={0.035} tone="warm" />
        <div className="relative max-w-[1480px] mx-auto px-6">
          <ChapterEyebrow label="Reserve your sitting" />
          <GoldRule width="3rem" align="center" className="mt-4 mb-8" />
          <DisplayHeadline size="hero" align="center" className="max-w-3xl mx-auto">
            Let's make something <em>quiet</em> together.
          </DisplayHeadline>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <GoldFoilButton href={tenantHref("/booking")}>Book a session</GoldFoilButton>
            <HairlineButton href={tenantHref("/contact")}>Write to us</HairlineButton>
          </div>
        </div>
      </section>
    </div>
  );
}

// Allow PublicLayout to wrap from the route. If the route mounts Home inside
// <PublicLayout>, the layout's nav + footer continue to surround this page.
Home.PublicLayout = PublicLayout;
